import cn from 'classnames';
import { useStoreAvailability } from '@utils/store/useStoreAvailabilityNew';
import { useUI } from '@context/GlobalUIContext';
import { usePdp } from '@context/PdpContext';
import { useStoresUI } from '@context/StoresContext';
import { parseAvailabilityMessage } from '@utils/parseAvailabilityMessage';
import { useLocale } from '@hooks/useLocale';
import { useSpuItemAvailabilityVariant } from '@hooks/useSpuItemAvailabilityVariant';
import Skeleton from 'react-loading-skeleton';
import SimpleButton from '_components/UI/SimpleButton';
import { FEATURE_FLAG_DISABLE_SPU } from '@config/feature-flag';
import FulfillmentStockLevelRow from '../FulfillmentStockLevelRow';

import styles from './StoreAvailabilityMessage.module.scss';

export interface StoreAvailabilityMessageProps {
  storeId: string;
  pickupMessage?: string;
  selectedAvailability: boolean;
}

const StoreAvailabilityMessage = ({
  storeId,
  pickupMessage,
  selectedAvailability,
}: StoreAvailabilityMessageProps) => {
  const { openStockLevelModal } = useUI();
  const { selectedVariant, shippingRestrictions, quantity } = usePdp();
  const { favouriteStoreDetails, setLocationAndFavouriteStore } = useStoresUI();
  const locale = useLocale();
  const result = useStoreAvailability(
    storeId,
    pickupMessage ?? null,
    locale ?? 'en',
    quantity ?? null
  );

  const { data } = useSpuItemAvailabilityVariant({
    selectedVariant: selectedVariant,
    productName: 'Product Name',
    shippingRestrictions: shippingRestrictions,
    quantity: quantity ?? null,
  });

  const availabilityMessageForStore = data?.find(
    (item) => item.store.data.mec_store_id === storeId
  );

  // Parse the availability message for clickable store name
  const parsedMessage = availabilityMessageForStore?.message?.short
    ? parseAvailabilityMessage(
        availabilityMessageForStore.message.short,
        locale
      )
    : null;

  if (!result) return null;

  const { stockMessage, availabilityMessage, stockLevelMessage, stockLevel } =
    result;

  if (!availabilityMessage) return null;

  return (
    <div>
      <span
        className={cn(styles.availabilityText, {
          [styles.availabilityTextInline]: !selectedAvailability,
        })}
      >
        {FEATURE_FLAG_DISABLE_SPU ? (
          stockMessage
        ) : (
          <div className={styles.availabilityMessageContainer}>
            <div>
              {parsedMessage ? (
                <>
                  <span>{parsedMessage?.beforeStore}</span>{' '}
                  <SimpleButton
                    variant="secondary"
                    mode="light"
                    className={styles.storeNameButton}
                    onClick={() => {
                      setLocationAndFavouriteStore(
                        favouriteStoreDetails.data.geo
                      );
                      openStockLevelModal();
                    }}
                  >
                    {parsedMessage?.storeName}
                  </SimpleButton>{' '}
                  {parsedMessage?.afterStore && (
                    <span>{parsedMessage?.afterStore}</span>
                  )}
                </>
              ) : (
                <>
                  <Skeleton
                    height={14}
                    width={180}
                    className={styles.availabilitySkeleton}
                  />
                  <Skeleton
                    height={14}
                    width={180}
                    className={styles.availabilitySkeleton}
                  />
                </>
              )}
            </div>
          </div>
        )}
        <FulfillmentStockLevelRow
          stockLevel={stockLevel}
          stockLevelMessage={stockLevelMessage}
          fitContent
        />
      </span>
    </div>
  );
};

export default StoreAvailabilityMessage;
